import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Tour } from 'antd';
import type { TourStepProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { CombinedStates, AppDispatch } from 'core/types';
import { buildTourSteps } from 'core/featureTour/steps';
import { completeFeatureTour } from 'core/featureTour/featureTourApi';
import { startTour, setStepIndex } from 'core/featureTour/featureTourSlice';
import { useAnchorReady } from 'core/featureTour/useAnchorReady';
import './featureTour.scss';

/**
 * Below this width the desktop nav is hidden (header.scss `max-width: 767px`),
 * so nav anchors exist only inside a closed Drawer.
 */
const DESKTOP_MIN_WIDTH = 768;

/**
 * The one-time guided feature tour.
 *
 * Mounted ONCE, globally (see core/routes.tsx), rather than per page: antd's
 * Tour can only target the current DOM, but this tour spans Dashboard → Sites →
 * site details. Mounting it above the router means it survives every navigation
 * and can simply render the slice of steps belonging to whatever page is on
 * screen, staying dormant (rendering nothing) on any other page.
 */
const FeatureTour: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    const { userData, isLoggedIn } = useSelector((state: CombinedStates) => state.user);
    const {
        sitesData, isFetching, isFetchingLastBooked, isFetchingSiteDetail,
    } = useSelector((state: CombinedStates) => state.sites);
    const { isRunning, stepIndex } = useSelector((state: CombinedStates) => state.featureTour);

    const accessLevel = userData?.role?.accessLevel;
    const hasSeen = !!userData?.hasSeenFeatureTour;

    const steps = useMemo(() => buildTourSteps(accessLevel), [accessLevel]);

    // Suppressed on small screens: the nav items live in a closed Drawer there,
    // so every nav step would highlight a hidden element. Not marking these
    // users as seen means they still get the tour on their next desktop login.
    const isDesktop = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches;

    // Start once the user is known, has steps, and hasn't seen it.
    useEffect(() => {
        if (isLoggedIn && !hasSeen && isDesktop && steps.length > 0 && !isRunning) {
            dispatch(startTour());
        }
    }, [isLoggedIn, hasSeen, isDesktop, steps.length, isRunning, dispatch]);

    const currentStep = steps[stepIndex];

    /**
     * Forward-only reconciliation on navigation: after a route change, jump to
     * the first remaining step that belongs to the new page. This is what makes
     * the tour robust to the user clicking the real button through the mask
     * cut-out instead of our Next — both land on the same step.
     */
    useEffect(() => {
        if (!isRunning || steps.length === 0) return;
        if (currentStep?.matchesRoute(location.pathname)) return;

        const nextIndex = steps.findIndex(
            (step, index) => index >= stepIndex && step.matchesRoute(location.pathname),
        );
        if (nextIndex >= 0 && nextIndex !== stepIndex) {
            dispatch(setStepIndex(nextIndex));
        }
    }, [location.pathname, isRunning, steps, stepIndex, currentStep, dispatch]);

    // Only the steps for the page currently on screen.
    const pageSteps = useMemo(
        () => steps.filter((step) => step.matchesRoute(location.pathname)),
        [steps, location.pathname],
    );

    const isOnThisPage = !!currentStep?.matchesRoute(location.pathname);

    const isPageDataLoading = !!isFetching || !!isFetchingLastBooked || !!isFetchingSiteDetail;

    const anchorStatus = useAnchorReady(
        isOnThisPage && currentStep ? currentStep.target : null,
        isRunning && isOnThisPage && !isPageDataLoading,
    );

    // Set while we navigate between pages, so the onClose antd fires during the
    // unmount isn't mistaken for the user dismissing the tour.
    const navigatingRef = useRef(false);
    // antd fires onFinish AND onClose on the final step, and both land here.
    // The thunk also guards against a repeat, but keeping the component itself
    // idempotent means correctness doesn't hinge on store-update timing.
    const markedRef = useRef(false);

    /** End the tour for good. Safe to call more than once. */
    const markSeenOnce = useCallback(() => {
        if (markedRef.current) return;
        markedRef.current = true;
        dispatch(completeFeatureTour());
    }, [dispatch]);

    /**
     * Pending "the user dismissed the tour" decision.
     *
     * rc-tour's finish button calls onClose FIRST and onFinish immediately
     * after (Tour.js: `onFinish: () => { handleClose(); onFinish?.(); }`), and
     * antd's own nextButtonProps.onClick runs later still. So at the moment
     * onClose fires there is no way to tell a real dismissal from a page
     * boundary. The close is therefore deferred by a microtask, and onFinish
     * cancels it when the tour is actually carrying on.
     */
    const pendingCloseRef = useRef(false);

    // Lets goToStep call itself when it has to skip a step (see the no-sites
    // branch) without becoming a self-referencing useCallback.
    const goToStepRef = useRef<((index: number) => void) | null>(null);

    const goToStep = useCallback((index: number) => {
        const target = steps[index];
        if (!target) {
            markSeenOnce();
            return;
        }

        dispatch(setStepIndex(index));

        if (target.matchesRoute(location.pathname)) return;

        // The next step lives on another page — move there and let this
        // component pick the tour back up once it renders.
        const from = steps[index - 1];
        let path = from?.nextRoute;

        if (path === 'sitesFirstSite') {
            const firstSite = sitesData?.[0];
            if (!firstSite) {
                // Nothing to open, so skip the steps that need a site rather
                // than ending the tour and losing everything after them.
                const resumeAt = steps.findIndex((step, i) => i >= index && !step.needsASite);
                if (resumeAt < 0) {
                    markSeenOnce();
                    return;
                }
                goToStepRef.current?.(resumeAt);
                return;
            }
            path = `/site/${firstSite.encryptedId}`;
        }

        if (path) {
            navigatingRef.current = true;
            navigate(path);
        }
    }, [steps, location.pathname, sitesData, navigate, dispatch, markSeenOnce]);

    goToStepRef.current = goToStep;

    const isLastGlobalStep = stepIndex >= steps.length - 1;

    /**
     * antd labels the last step of the array it is given "Finish" and calls
     * onFinish. That array is only the CURRENT PAGE's slice, so on the dashboard
     * it fires while four more steps are still to come on other pages. Treat it
     * as "finish" only at the end of the whole list; otherwise carry on.
     */
    const handleFinish = useCallback(() => {
        // Cancel the close that rc-tour fired a moment ago on our behalf.
        pendingCloseRef.current = false;

        if (navigatingRef.current) {
            navigatingRef.current = false;
            return;
        }
        if (!isLastGlobalStep) {
            goToStep(stepIndex + 1);
            return;
        }
        markSeenOnce();
    }, [isLastGlobalStep, stepIndex, goToStep, markSeenOnce]);

    /** The X, Esc, the mask — or the finish button, which we can't yet tell apart. */
    const handleClose = useCallback(() => {
        if (navigatingRef.current) {
            navigatingRef.current = false;
            return;
        }

        pendingCloseRef.current = true;
        Promise.resolve().then(() => {
            if (!pendingCloseRef.current) return;
            pendingCloseRef.current = false;
            markSeenOnce();
        });
    }, [markSeenOnce]);

    // Apply the step's missing-anchor policy so the tour can never hang.
    useEffect(() => {
        if (!isRunning || !isOnThisPage || !currentStep || anchorStatus !== 'timeout') return;
        if (currentStep.onMissing === 'skip') {
            goToStep(stepIndex + 1);
        }
        // 'centre' and 'fallback' are handled when building the antd steps below.
    }, [anchorStatus, isRunning, isOnThisPage, currentStep, stepIndex, goToStep]);

    const antdSteps: TourStepProps[] = useMemo(() => pageSteps.map((step, index) => {
        const useFallback = anchorStatus === 'timeout' && step.onMissing === 'fallback';
        const selector = useFallback ? step.fallbackTarget : step.target;
        const centreOnly = anchorStatus === 'timeout' && step.onMissing === 'centre';

        // antd would label the last step of THIS page "Finish". When the tour
        // carries on to another page, say "Next" instead so the button matches
        // what it actually does.
        const isPageLast = index === pageSteps.length - 1;
        const globalIndexOfStep = steps.findIndex((s) => s.id === step.id);
        const continuesElsewhere = isPageLast && globalIndexOfStep < steps.length - 1;

        const base: TourStepProps = {
            title: step.title,
            description: step.description,
            // Default `block: 'start'` tucks targets under the sticky header.
            scrollIntoViewOptions: { block: 'center' as const },
            ...(continuesElsewhere ? { nextButtonProps: { children: 'Next' } } : {}),
        };

        // Omitting `target` entirely (rather than passing undefined) is what
        // makes antd centre the card with no highlight.
        if (centreOnly) return base;

        return {
            ...base,
            // A function target is resolved by antd at display time, so it picks
            // up an element that appeared after this array was built.
            target: () => (selector ? document.querySelector(selector) : null) as HTMLElement,
        };
    }), [pageSteps, anchorStatus, steps]);

    const localIndex = pageSteps.findIndex((step) => step.id === currentStep?.id);

    // Don't open over a modal/drawer — the popover would render underneath it.
    const overlayOpen = typeof document !== 'undefined'
        && !!document.querySelector('.ant-modal-root, .ant-drawer-open');

    // `isRunning` alone is not enough to render: it is persisted, so it can
    // arrive as true from an earlier session on this browser even though the
    // user has since finished the tour elsewhere, or has switched to a screen
    // too narrow to place the nav steps. The user flag and the breakpoint are
    // the authority, and they are re-checked here rather than only at start.
    const canRun = isRunning && !hasSeen && isDesktop && steps.length > 0;

    const open = canRun
        && isOnThisPage
        && localIndex >= 0
        && anchorStatus !== 'pending'
        && !overlayOpen;

    if (!canRun || localIndex < 0) return null;

    return (
        <Tour
            // Remount per page rather than mutating steps mid-flight, which is
            // where antd Tour mis-positions.
            key={location.pathname}
            open={open}
            steps={antdSteps}
            current={localIndex}
            onChange={(next) => goToStep(stepIndex + (next - localIndex))}
            onFinish={handleFinish}
            onClose={handleClose} />
    );
};

export default FeatureTour;
